/**
 * Form Configuration System
 *
 * Defines how forms should render based on Sport Type + Category + Item Type
 */

export type SportType = 'FOOTBALL' | 'SOCCER' | 'BASKETBALL' | 'BASEBALL' | 'TENNIS' | 'GOLF' | 'OTHER'
export type Category = 'CLOTHING' | 'FOOTWEAR' | 'EQUIPMENT' | 'ACCESSORIES'
export type Condition = 'GAME_WORN' | 'WARMUP_WORN' | 'PLAYER_ISSUED' | 'UNUSED' | 'DAMAGED'

export interface FieldDefinition {
  name: string
  label: string
  type: 'text' | 'number' | 'date' | 'select' | 'textarea'
  placeholder?: string
  required?: boolean
  options?: { label: string; value: string }[]
}

export interface FieldGroup {
  name: string
  title: string
  icon?: string
  fields: FieldDefinition[]
}

export interface ItemTypeConfig {
  label: string
  value: string
  groups: string[] // References to field group names
  hasStats: boolean
  playerSpecific: boolean
  customFields?: FieldDefinition[]
  statFields?: FieldDefinition[]
}

export interface SportConfig {
  label: string
  value: SportType
  icon: string
  numberLabel: string // "Jersey Number", "Kit Number", "Uniform Number"
  teams?: { label: string; value: string }[]
  leagues: string[]
  itemTypes?: {
    [category: string]: ItemTypeConfig[]
  }
}

// ============================================
// REUSABLE FIELD GROUPS
// ============================================

export const FIELD_GROUPS: Record<string, FieldGroup> = {
  player: {
    name: 'player',
    title: 'Player Information',
    icon: 'user',
    fields: [
      { name: 'playerName', label: 'Player Name', type: 'text', required: true },
      { name: 'number', label: 'Number', type: 'text', required: false },
      { name: 'position', label: 'Position', type: 'text', required: false }
    ]
  },

  team: {
    name: 'team',
    title: 'Team Information',
    icon: 'users',
    fields: [
      { name: 'team', label: 'Team/Club', type: 'text', required: true },
      { name: 'season', label: 'Season/Year', type: 'text', required: false }
    ]
  },

  event: {
    name: 'event',
    title: 'Event/Game Information',
    icon: 'calendar',
    fields: [
      { name: 'eventTitle', label: 'Event Title', type: 'text', required: false, placeholder: 'Super Bowl LIX, Champions League Final, etc.' },
      { name: 'eventDate', label: 'Event Date', type: 'date', required: false },
      { name: 'opponent', label: 'Opponent', type: 'text', required: false },
      { name: 'venue', label: 'Venue', type: 'text', required: false }
    ]
  },

  clothingSize: {
    name: 'clothingSize',
    title: 'Size',
    fields: [
      {
        name: 'size',
        label: 'Size',
        type: 'select',
        required: true,
        options: [
          { label: 'XS', value: 'XS' },
          { label: 'S', value: 'S' },
          { label: 'M', value: 'M' },
          { label: 'L', value: 'L' },
          { label: 'XL', value: 'XL' },
          { label: 'XXL', value: 'XXL' },
          { label: 'XXXL', value: 'XXXL' }
        ]
      }
    ]
  },

  footwearSize: {
    name: 'footwearSize',
    title: 'Footwear Details',
    fields: [
      { name: 'shoeSize', label: 'Shoe Size', type: 'text', required: true, placeholder: '10.5' },
      { name: 'shoeModel', label: 'Model', type: 'text', required: false, placeholder: 'Nike Air Jordan 1' },
      { name: 'brand', label: 'Brand', type: 'text', required: false }
    ]
  },

  equipmentSpecs: {
    name: 'equipmentSpecs',
    title: 'Equipment Specifications',
    fields: [
      { name: 'brand', label: 'Brand', type: 'text', required: false },
      { name: 'model', label: 'Model', type: 'text', required: false },
      { name: 'specifications', label: 'Specifications', type: 'textarea', required: false, placeholder: 'Technical details, measurements, etc.' }
    ]
  }
}

// ============================================
// SPORT CONFIGURATIONS
// ============================================

const NFL_TEAMS = [
  { label: 'New England Patriots', value: 'patriots' },
  { label: 'Tampa Bay Buccaneers', value: 'buccaneers' },
  { label: 'Kansas City Chiefs', value: 'chiefs' },
  { label: 'Green Bay Packers', value: 'packers' },
  { label: 'Dallas Cowboys', value: 'cowboys' },
  { label: 'San Francisco 49ers', value: '49ers' },
  { label: 'Pittsburgh Steelers', value: 'steelers' },
  { label: 'Buffalo Bills', value: 'bills' }
]

const NBA_TEAMS = [
  { label: 'Los Angeles Lakers', value: 'lakers' },
  { label: 'Boston Celtics', value: 'celtics' },
  { label: 'Golden State Warriors', value: 'warriors' },
  { label: 'Chicago Bulls', value: 'bulls' },
  { label: 'Miami Heat', value: 'heat' },
  { label: 'Brooklyn Nets', value: 'nets' }
]

const MLB_TEAMS = [
  { label: 'New York Yankees', value: 'yankees' },
  { label: 'Los Angeles Dodgers', value: 'dodgers' },
  { label: 'Boston Red Sox', value: 'redsox' },
  { label: 'Chicago Cubs', value: 'cubs' },
  { label: 'San Francisco Giants', value: 'giants' }
]

export const SPORT_CONFIGS: Record<SportType, SportConfig> = {
  FOOTBALL: {
    label: 'Football',
    value: 'FOOTBALL',
    icon: '🏈',
    numberLabel: 'Jersey Number',
    teams: NFL_TEAMS,
    leagues: ['NFL', 'College Football', 'High School'],
    itemTypes: {
      CLOTHING: [
        {
          label: 'Jersey/Uniform',
          value: 'jersey',
          groups: ['player', 'team', 'clothingSize', 'event'],
          hasStats: true,
          playerSpecific: true,
          statFields: [
            { name: 'passingYards', label: 'Passing Yards', type: 'number' },
            { name: 'touchdowns', label: 'Touchdowns', type: 'number' },
            { name: 'completions', label: 'Completions', type: 'number' },
            { name: 'rushingYards', label: 'Rushing Yards', type: 'number' },
            { name: 'tackles', label: 'Tackles', type: 'number' }
          ]
        }
      ],
      EQUIPMENT: [
        {
          label: 'Helmet',
          value: 'helmet',
          groups: ['player', 'team', 'equipmentSpecs'],
          hasStats: false,
          playerSpecific: true,
          customFields: [
            { name: 'helmetSize', label: 'Helmet Size', type: 'text', placeholder: 'Medium, Large, etc.' }
          ]
        },
        {
          label: 'Game Ball',
          value: 'ball',
          groups: ['event', 'equipmentSpecs'],
          hasStats: false,
          playerSpecific: false,
          customFields: [
            { name: 'homeTeam', label: 'Home Team', type: 'text' },
            { name: 'awayTeam', label: 'Away Team', type: 'text' },
            { name: 'finalScore', label: 'Final Score', type: 'text', placeholder: '28-24' },
            { name: 'significance', label: 'Significance', type: 'textarea', placeholder: 'Game-winning touchdown, record-breaking play, etc.' }
          ]
        }
      ],
      FOOTWEAR: [
        {
          label: 'Cleats',
          value: 'cleats',
          groups: ['player', 'team', 'footwearSize', 'event'],
          hasStats: true,
          playerSpecific: true
        }
      ],
      ACCESSORIES: [
        {
          label: 'Gloves',
          value: 'gloves',
          groups: ['player', 'team', 'event'],
          hasStats: false,
          playerSpecific: true,
          customFields: [
            { name: 'gloveSize', label: 'Glove Size', type: 'text' }
          ]
        }
      ]
    }
  },

  SOCCER: {
    label: 'Soccer',
    value: 'SOCCER',
    icon: '⚽',
    numberLabel: 'Kit Number',
    leagues: ['Premier League', 'La Liga', 'Champions League', 'MLS', 'Serie A', 'Bundesliga'],
    itemTypes: {
      CLOTHING: [
        {
          label: 'Jersey/Kit',
          value: 'kit',
          groups: ['player', 'team', 'clothingSize', 'event'],
          hasStats: true,
          playerSpecific: true,
          statFields: [
            { name: 'goals', label: 'Goals', type: 'number' },
            { name: 'assists', label: 'Assists', type: 'number' },
            { name: 'minutesPlayed', label: 'Minutes Played', type: 'number' },
            { name: 'cleanSheets', label: 'Clean Sheets (GK)', type: 'number' }
          ]
        }
      ],
      EQUIPMENT: [
        {
          label: 'Match Ball',
          value: 'ball',
          groups: ['event', 'equipmentSpecs'],
          hasStats: false,
          playerSpecific: false,
          customFields: [
            { name: 'homeTeam', label: 'Home Team', type: 'text' },
            { name: 'awayTeam', label: 'Away Team', type: 'text' },
            { name: 'finalScore', label: 'Final Score', type: 'text', placeholder: '3-2' },
            { name: 'competition', label: 'Competition', type: 'text', placeholder: 'Champions League, World Cup, etc.' }
          ]
        }
      ],
      FOOTWEAR: [
        {
          label: 'Boots/Cleats',
          value: 'boots',
          groups: ['player', 'team', 'footwearSize', 'event'],
          hasStats: true,
          playerSpecific: true
        }
      ]
    }
  },

  BASKETBALL: {
    label: 'Basketball',
    value: 'BASKETBALL',
    icon: '🏀',
    numberLabel: 'Jersey Number',
    teams: NBA_TEAMS,
    leagues: ['NBA', 'NCAA', 'G League', 'EuroLeague'],
    itemTypes: {
      CLOTHING: [
        {
          label: 'Jersey',
          value: 'jersey',
          groups: ['player', 'team', 'clothingSize', 'event'],
          hasStats: true,
          playerSpecific: true,
          statFields: [
            { name: 'points', label: 'Points', type: 'number' },
            { name: 'rebounds', label: 'Rebounds', type: 'number' },
            { name: 'assists', label: 'Assists', type: 'number' },
            { name: 'steals', label: 'Steals', type: 'number' },
            { name: 'blocks', label: 'Blocks', type: 'number' }
          ]
        }
      ],
      EQUIPMENT: [
        {
          label: 'Game Ball',
          value: 'ball',
          groups: ['event', 'equipmentSpecs'],
          hasStats: false,
          playerSpecific: false,
          customFields: [
            { name: 'homeTeam', label: 'Home Team', type: 'text' },
            { name: 'awayTeam', label: 'Away Team', type: 'text' },
            { name: 'finalScore', label: 'Final Score', type: 'text', placeholder: '108-102' }
          ]
        }
      ],
      FOOTWEAR: [
        {
          label: 'Sneakers',
          value: 'sneakers',
          groups: ['player', 'team', 'footwearSize', 'event'],
          hasStats: true,
          playerSpecific: true
        }
      ]
    }
  },

  BASEBALL: {
    label: 'Baseball',
    value: 'BASEBALL',
    icon: '⚾',
    numberLabel: 'Uniform Number',
    teams: MLB_TEAMS,
    leagues: ['MLB', 'Minor League', 'College Baseball'],
    itemTypes: {
      CLOTHING: [
        {
          label: 'Jersey/Uniform',
          value: 'jersey',
          groups: ['player', 'team', 'clothingSize', 'event'],
          hasStats: true,
          playerSpecific: true,
          statFields: [
            { name: 'hits', label: 'Hits', type: 'number' },
            { name: 'homeRuns', label: 'Home Runs', type: 'number' },
            { name: 'rbi', label: 'RBIs', type: 'number' },
            { name: 'battingAverage', label: 'Batting Average', type: 'text', placeholder: '.300' },
            { name: 'era', label: 'ERA (Pitcher)', type: 'text', placeholder: '2.50' },
            { name: 'strikeouts', label: 'Strikeouts', type: 'number' }
          ]
        }
      ],
      EQUIPMENT: [
        {
          label: 'Game Ball',
          value: 'ball',
          groups: ['event', 'equipmentSpecs'],
          hasStats: false,
          playerSpecific: false,
          customFields: [
            { name: 'homeTeam', label: 'Home Team', type: 'text' },
            { name: 'awayTeam', label: 'Away Team', type: 'text' },
            { name: 'finalScore', label: 'Final Score', type: 'text', placeholder: '5-3' },
            { name: 'pitcher', label: 'Pitcher', type: 'text' },
            { name: 'batter', label: 'Batter', type: 'text' },
            { name: 'significance', label: 'What Happened?', type: 'textarea', placeholder: 'Walk-off home run, perfect game, etc.' }
          ]
        },
        {
          label: 'Bat',
          value: 'bat',
          groups: ['player', 'team', 'equipmentSpecs'],
          hasStats: true,
          playerSpecific: true,
          customFields: [
            { name: 'batLength', label: 'Bat Length (inches)', type: 'text', placeholder: '34"' },
            { name: 'batWeight', label: 'Bat Weight (oz)', type: 'text', placeholder: '32 oz' }
          ]
        }
      ],
      ACCESSORIES: [
        {
          label: 'Batting Gloves',
          value: 'gloves',
          groups: ['player', 'team', 'event'],
          hasStats: false,
          playerSpecific: true,
          customFields: [
            { name: 'gloveSize', label: 'Glove Size', type: 'text' }
          ]
        }
      ]
    }
  },

  TENNIS: {
    label: 'Tennis',
    value: 'TENNIS',
    icon: '🎾',
    numberLabel: 'N/A',
    leagues: ['Grand Slam', 'ATP Tour', 'WTA Tour'],
    itemTypes: {
      CLOTHING: [
        {
          label: 'Match Outfit',
          value: 'outfit',
          groups: ['player', 'clothingSize', 'event'],
          hasStats: false,
          playerSpecific: true,
          customFields: [
            { name: 'tournament', label: 'Tournament', type: 'text', placeholder: 'Wimbledon, US Open, etc.' },
            { name: 'round', label: 'Round', type: 'text', placeholder: 'Finals, Semifinals, etc.' }
          ]
        }
      ],
      EQUIPMENT: [
        {
          label: 'Racket',
          value: 'racket',
          groups: ['player', 'equipmentSpecs'],
          hasStats: false,
          playerSpecific: true,
          customFields: [
            { name: 'racketModel', label: 'Racket Model', type: 'text' },
            { name: 'stringTension', label: 'String Tension', type: 'text' }
          ]
        }
      ]
    }
  },

  GOLF: {
    label: 'Golf',
    value: 'GOLF',
    icon: '⛳',
    numberLabel: 'N/A',
    leagues: ['PGA Tour', 'LPGA Tour', 'Masters'],
    itemTypes: {
      EQUIPMENT: [
        {
          label: 'Club',
          value: 'club',
          groups: ['player', 'equipmentSpecs'],
          hasStats: false,
          playerSpecific: true,
          customFields: [
            { name: 'clubType', label: 'Club Type', type: 'text', placeholder: 'Driver, Putter, etc.' }
          ]
        }
      ]
    }
  },

  OTHER: {
    label: 'Other Sport',
    value: 'OTHER',
    icon: '🏆',
    numberLabel: 'Number',
    leagues: [],
    itemTypes: {
      CLOTHING: [
        {
          label: 'Generic Item',
          value: 'generic',
          groups: ['player', 'team', 'clothingSize', 'event'],
          hasStats: false,
          playerSpecific: true
        }
      ],
      EQUIPMENT: [
        {
          label: 'Generic Equipment',
          value: 'generic',
          groups: ['equipmentSpecs', 'event'],
          hasStats: false,
          playerSpecific: false
        }
      ]
    }
  }
}

// ============================================
// HELPER FUNCTIONS
// ============================================

export function getItemTypeConfig(
  sport: SportType,
  category: Category,
  itemType?: string
): ItemTypeConfig | null {
  const sportConfig = SPORT_CONFIGS[sport]
  if (!sportConfig.itemTypes || !sportConfig.itemTypes[category]) return null

  const itemTypes = sportConfig.itemTypes[category]
  if (!itemType && itemTypes.length > 0) return itemTypes[0] // Return first if no specific type

  return itemTypes.find(it => it.value === itemType) || null
}

export function getAvailableItemTypes(sport: SportType, category: Category): ItemTypeConfig[] {
  const sportConfig = SPORT_CONFIGS[sport]
  return sportConfig.itemTypes?.[category] || []
}
